import '@ooka_default/styles/main.scss'
import '@styles/chrome-bug.css'
import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'
import 'swiper/swiper-bundle.css'
import { FC, useEffect, useState } from 'react'
import type { AppProps } from 'next/app'
import Script from 'next/script'
import { appWithTranslation } from 'next-i18next'

import { ManagedUIContext } from '@corratech/pylot-ui/context'
import { Head } from '@components/common'
import { Toast } from '@corratech/pylot-ui'
import { TagContextInit } from '@corratech/pylot-tag-manager'
import { useGTM } from '@corratech/pylot-gtm'
import { LoadingIndicator } from '@components/LoadingIndicator'
import { useStoreConfig } from '@config/hooks/useStoreConfig'
import dynamic from 'next/dynamic'
import { Maintenance } from '@components/Maintenance'
import { AlfakherContext } from 'components/alfakherContext/AlfakherContext'
import { useCookies } from 'react-cookie'
import { getDomainName } from 'preload'

require('polyfill-object.fromentries')

const Noop: FC = ({ children }) => <>{children}</>

function MyApp({ Component, pageProps }: AppProps) {
    const Layout = (Component as any).Layout || Noop
    const authRequired = (Component as any).Protected || false
    const config = useStoreConfig()
    const [cookies, setCookie] = useCookies(['cje'])
    const [isLoader, setIsLoader] = useState(false)

    const Signifyd = dynamic(
        () => import('@components/alfakher/pylot-signifyd/src/Signifyd')
    )

    const setCJCookie = () => {
        const params = new URLSearchParams(window.location.search)
        const cjevent = params.get('cjevent')
        if (cjevent) {
            const dt = new Date()
            const baseUrl = config?.base?.url?.baseUrl
            const domain = `.${getDomainName(baseUrl)}`
            setCookie('cje', cjevent, {
                path: '/',
                httpOnly: false,
                secure: true,
                sameSite: 'none',
                expires: new Date(dt.getTime() + 569401 * 60000),
                domain: domain
            })
        }
    }

    useEffect(() => {
        setCJCookie()
        document.body.classList?.remove('loading')
    }, [])

    useEffect(() => {
        window.hsConversationsSettings = { loadImmediately: false }
    }, [])

    const { gtmHandler, gtmInitScript } = useGTM()

    return (
        <>
            <Head />
            {config.base.signifyd.enabled === true && <Signifyd />}
            <Script
                strategy="lazyOnload"
                id="hs-script-loader"
                async
                defer
                src={config.base?.hubspot?.chatUrl}
            />
            {isLoader && <LoadingIndicator isHubSpotLoader />}
            <Maintenance isMaintenanceEnabled>
                <TagContextInit
                    handlers={[gtmHandler]}
                    scripts={[gtmInitScript]}
                >
                    <ManagedUIContext LoadingIndicator={LoadingIndicator}>
                        <AlfakherContext>
                            <Layout
                                pageProps={pageProps}
                                authRequired={authRequired}
                                setIsLoader={setIsLoader}
                            >
                                <Toast />
                                <Component {...pageProps} />
                            </Layout>
                        </AlfakherContext>
                    </ManagedUIContext>
                </TagContextInit>
            </Maintenance>
        </>
    )
}

export default appWithTranslation(MyApp)
